import { readCommunity } from "@/features/community/records";
import type {
  LeagueEpisodeThumbnail,
  LeagueLatestReplay,
  LeagueSummary,
  V2EpisodeDetail,
} from "@softmax/observatory-client/types";

import { type PreviewMediaItem } from "../content/preview-details.types";
async function readLeagueThumbnails({
  signal,
  leagueIds,
}: {
  readonly signal?: AbortSignal | undefined;
  readonly leagueIds: readonly string[];
}) {
  const query = new URLSearchParams();

  for (const id of [...new Set(leagueIds)].slice(0, 50).toSorted())
    query.append("league_id", id);

  return readCommunity<LeagueEpisodeThumbnail[]>({
    signal,
    path: `/v2/leagues/episode-thumbnails?${query}`,
  });
}
export async function readLatestReplay({
  signal,
  leagueIds,
}: {
  readonly signal?: AbortSignal | undefined;
  readonly leagueIds: readonly string[];
}): Promise<PreviewMediaItem | null> {
  const ids = [...new Set(leagueIds)].slice(0, 50);

  for (let offset = 0; offset < ids.length; offset += 10) {
    const batch = ids.slice(offset, offset + 10);
    const query = new URLSearchParams();

    for (const id of batch) query.append("league_id", id);

    const result = await readCommunity<LeagueLatestReplay[]>({
      path: `/v2/leagues/latest-replays?${query}`,
      signal,
    });

    if (result.status !== "ok") continue;

    const selected = batch
      .map((id) => result.data.find((replay) => replay.league_id === id))
      .find((replay) => replay !== undefined);

    if (!selected) continue;

    return readReplayMedia({ selected, signal });
  }

  return null;
}

async function readReplayMedia({
  selected,
  signal,
}: {
  readonly selected: LeagueLatestReplay;
  readonly signal: AbortSignal | undefined;
}): Promise<PreviewMediaItem> {
  const [thumbs, detail] = await Promise.all([
    readLeagueThumbnails({ signal, leagueIds: [selected.league_id] }),
    readCommunity<V2EpisodeDetail>({
      signal,
      path: `/v2/episodes/${encodeURIComponent(selected.episode_id)}`,
    }),
  ]);
  const thumbnail =
    thumbs.status === "ok"
      ? thumbs.data.find((row) => row.episode_id === selected.episode_id)
      : undefined;
  return {
    kind: "replay",
    src: `replay:${selected.episode_id}`,
    episodeId: selected.episode_id,
    coworldVersion:
      detail.status === "ok" ? (detail.data.coworld_version ?? null) : null,
    poster: thumbnail?.thumbnail_url ?? null,
    alt: `Episode ${selected.episode_id.slice(0, 8)}`,
  };
}
export async function readLeagueMatchFrames({
  signal,
  leagues,
  leagueId,
}: {
  readonly signal?: AbortSignal | undefined;
  readonly leagues: readonly LeagueSummary[];
  readonly leagueId: string;
}): Promise<PreviewMediaItem[]> {
  const ids = [leagueId, ...leagues.map((l) => l.id)];
  const thumbs = await readLeagueThumbnails({
    signal,
    leagueIds: ids,
  });

  if (thumbs.status !== "ok") return [];

  const frames: PreviewMediaItem[] = [];

  for (const id of new Set(ids)) {
    const row = thumbs.data.find((t) => t.league_id === id);

    if (!row?.thumbnail_url) continue;

    const name = leagues.find((l) => l.id === id)?.name ?? "Latest match";
    frames.push({
      kind: "capture",
      src: row.thumbnail_url,
      alt: `${name}, latest match`,
    });
  }

  return frames;
}
